import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/layout/PageHeader";
import { CHATBOT } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Copy, Sparkles, Link as LinkIcon } from "lucide-react";

export default function Chatbot() {
  const { user } = useAuth();
  const { data: widget } = useQuery({
    queryKey: ["div-widget", user?.division_id],
    queryFn: () => api.get(`/divisions/${user.division_id}/widget`).then((r) => r.data),
    enabled: !!user?.division_id,
  });
  const slug = widget?.widget_slug;
  const publicUrl = slug ? `${window.location.origin}/w/${slug}` : `${window.location.origin}/chat/${user?.division_id}`;

  return (
    <div>
      <PageHeader
        overline="AI Assistant"
        title="Chatbot"
        subtitle="Trained on your uploaded docs. Grounded, escalates when uncertain."
        actions={<Button data-testid="chatbot-copy-url" variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Chat URL copied"); }}><Copy className="h-4 w-4 mr-2" />Copy Chat URL</Button>}
      />
      <div className="px-6 lg:px-10 pb-10">
        <Tabs defaultValue="config">
          <TabsList className="rounded-sm bg-secondary/40">
            <TabsTrigger value="config" className="rounded-sm">Configuration</TabsTrigger>
            <TabsTrigger value="knowledge" className="rounded-sm">Knowledge Base</TabsTrigger>
            <TabsTrigger value="embed" className="rounded-sm">Embed</TabsTrigger>
            <TabsTrigger value="preview" className="rounded-sm">Live Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="config" className="mt-6"><ConfigTab /></TabsContent>
          <TabsContent value="knowledge" className="mt-6"><KnowledgeTab /></TabsContent>
          <TabsContent value="embed" className="mt-6"><EmbedTab publicUrl={publicUrl} slug={slug} divisionId={user?.division_id} /></TabsContent>
          <TabsContent value="preview" className="mt-6"><PreviewTab publicUrl={publicUrl} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ConfigTab() {
  const { data: cfg } = useQuery({ queryKey: ["cfg"], queryFn: () => api.get("/chatbot/config").then((r) => r.data) });
  const [state, setState] = useState(null);
  React.useEffect(() => { if (cfg && !state) setState(cfg); }, [cfg, state]);
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (payload) => api.put("/chatbot/config", payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cfg"] }); toast.success("Configuration saved"); },
  });
  if (!state) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const FONTS = ["System (default)", "Inter", "Cabinet Grotesk", "Satoshi", "Georgia", "Menlo"];
  const ANIMATIONS = [
    { key: "fade", label: "Fade in" },
    { key: "slide", label: "Slide up" },
    { key: "bounce", label: "Bounce" },
    { key: "none", label: "No animation" },
  ];
  const BUBBLES = [
    { key: "rounded", label: "Rounded" },
    { key: "square", label: "Square" },
    { key: "pill", label: "Pill" },
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="rounded-sm bg-card border-border p-6" data-testid="chatbot-config-basics">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Basics</div>
        <div className="font-display text-xl font-extrabold mb-4">Voice &amp; welcome</div>
        <div className="space-y-4">
          <div><Label>Welcome message</Label><Textarea rows={2} className="rounded-sm mt-1.5 bg-secondary/40" value={state.welcome_message || ""} onChange={(e) => setState({ ...state, welcome_message: e.target.value })} data-testid="chatbot-welcome" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tone</Label>
              <Select value={state.tone} onValueChange={(v) => setState({ ...state, tone: v })}>
                <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="chatbot-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="sales-oriented">Sales-oriented</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Primary color</Label><Input type="color" className="rounded-sm mt-1.5 h-10 bg-secondary/40" value={state.primary_color || "#007AFF"} onChange={(e) => setState({ ...state, primary_color: e.target.value })} data-testid="chatbot-primary-color" /></div>
          </div>
          <div>
            <Label>Suggested Questions (one per line)</Label>
            <Textarea rows={3} className="rounded-sm mt-1.5 bg-secondary/40" value={(state.suggested_questions || []).join("\n")} onChange={(e) => setState({ ...state, suggested_questions: e.target.value.split("\n").filter(Boolean) })} data-testid="chatbot-suggested" />
          </div>
        </div>
      </Card>

      <Card className="rounded-sm bg-card border-border p-6" data-testid="chatbot-config-theme">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Widget theme</div>
        <div className="font-display text-xl font-extrabold mb-4">Brand your chat widget</div>
        <div className="space-y-4">
          <div><Label>Font family</Label>
            <Select value={state.font_family || "System (default)"} onValueChange={(v) => setState({ ...state, font_family: v === "System (default)" ? null : v })}>
              <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="chatbot-font-family"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Bot avatar URL <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label>
            <Input className="rounded-sm mt-1.5 bg-secondary/40" placeholder="https://…/avatar.png" value={state.bot_avatar_url || ""} onChange={(e) => setState({ ...state, bot_avatar_url: e.target.value })} data-testid="chatbot-avatar" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Greeting animation</Label>
              <Select value={state.greeting_animation || "fade"} onValueChange={(v) => setState({ ...state, greeting_animation: v })}>
                <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="chatbot-animation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ANIMATIONS.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Message bubbles</Label>
              <Select value={state.bubble_style || "rounded"} onValueChange={(v) => setState({ ...state, bubble_style: v })}>
                <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="chatbot-bubble-style"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BUBBLES.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Logo URL <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label>
            <Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.logo_url || ""} onChange={(e) => setState({ ...state, logo_url: e.target.value })} placeholder="https://…" data-testid="chatbot-logo" />
          </div>
        </div>
      </Card>

      <Card className="rounded-sm bg-card border-border p-4 col-span-full">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-muted-foreground rounded-sm bg-secondary/20 border border-border p-3 flex-1 min-w-[280px]">
            <div className="font-bold text-[10px] uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Natural visitor capture</div>
            Every new visitor is asked for their name and email <b>conversationally</b> after their first message — no forms. The bot then answers their question using your knowledge base and escalates to a human when needed.
          </div>
          <Button data-testid={CHATBOT.saveConfig} className="rounded-sm bg-primary" onClick={() => mut.mutate({
            welcome_message: state.welcome_message,
            tone: state.tone,
            primary_color: state.primary_color,
            logo_url: state.logo_url,
            suggested_questions: state.suggested_questions,
            font_family: state.font_family,
            bot_avatar_url: state.bot_avatar_url,
            greeting_animation: state.greeting_animation,
            bubble_style: state.bubble_style,
          })} disabled={mut.isPending}>Save Configuration</Button>
        </div>
      </Card>
    </div>
  );
}

function KnowledgeTab() {
  const { data: docs = [] } = useQuery({ queryKey: ["docs"], queryFn: () => api.get("/chatbot/docs").then((r) => r.data) });
  const qc = useQueryClient();
  const fileRef = useRef();

  const upload = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post("/chatbot/docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["docs"] }); toast.success("Uploaded"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Upload failed"),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/chatbot/docs/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["docs"] }); toast.success("Deleted"); } });

  return (
    <Card className="rounded-sm bg-card border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Knowledge base</div>
          <div className="font-display text-xl font-extrabold">Upload docs (PDF, DOCX, CSV, TXT)</div>
          <div className="text-xs text-muted-foreground mt-1">The AI Provider set in Settings → AI Provider will use these docs as grounded context for every visitor answer.</div>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.csv,.txt,.md" className="hidden" data-testid={CHATBOT.uploadInput}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) { upload.mutate(f); e.target.value = ""; } }} />
          <Button onClick={() => fileRef.current?.click()} className="rounded-sm bg-primary" data-testid="chatbot-upload-btn"><Upload className="h-4 w-4 mr-2" />Upload</Button>
        </div>
      </div>
      <div className="space-y-2" data-testid="chatbot-docs-list">
        {docs.length === 0 && <div className="text-sm text-muted-foreground text-center py-8" data-testid="chatbot-docs-empty">No documents uploaded yet. The chatbot will only greet visitors until you upload knowledge.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 p-3 rounded-sm border border-border bg-secondary/20" data-testid={`chatbot-doc-${d.id}`}>
            <FileText className="h-4 w-4 text-primary" />
            <div className="flex-1">
              <div className="text-sm font-medium">{d.filename}</div>
              <div className="text-xs text-muted-foreground">{(d.size / 1024).toFixed(1)} KB · {new Date(d.uploaded_at).toLocaleDateString()}</div>
            </div>
            <button onClick={() => del.mutate(d.id)} className="text-muted-foreground hover:text-red-400" data-testid={`chatbot-doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function EmbedTab({ publicUrl, slug, divisionId }) {
  const [slugInput, setSlugInput] = useState(slug || "");
  React.useEffect(() => { setSlugInput(slug || ""); }, [slug]);
  const qc = useQueryClient();
  const rename = useMutation({
    mutationFn: () => api.put(`/divisions/${divisionId}/widget-slug`, { widget_slug: slugInput }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["div-widget"] }); toast.success("Widget URL updated"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Rename failed"),
  });

  const snippet = `<!-- LeadifyCRM Chat Widget -->
<script>
(function(){
  var d=document,s=d.createElement('iframe');
  s.src='${publicUrl}?embed=1';
  s.style.cssText='position:fixed;bottom:20px;right:20px;width:400px;height:600px;border:0;z-index:9999;border-radius:12px;box-shadow:0 20px 40px rgba(0,0,0,0.2)';
  s.title='LeadifyCRM Chat';
  d.body.appendChild(s);
})();
</script>`;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="rounded-sm bg-card border-border p-6" data-testid="widget-slug-card">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><LinkIcon className="h-3 w-3" /> Widget URL slug</div>
        <div className="font-display text-xl font-extrabold mb-3">Your public embed address</div>
        <div className="text-sm text-muted-foreground mb-3">A short, URL-safe identifier for this division. Change it to something memorable — customers see <span className="font-mono text-primary">/w/{slugInput || "…"}</span>.</div>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center rounded-sm bg-secondary/40 border border-border overflow-hidden">
            <span className="px-3 text-xs text-muted-foreground border-r border-border">/w/</span>
            <Input value={slugInput} onChange={(e) => setSlugInput(e.target.value.toLowerCase())} className="border-0 rounded-none bg-transparent focus-visible:ring-0" data-testid="widget-slug-input" />
          </div>
          <Button className="rounded-sm bg-primary" onClick={() => rename.mutate()} disabled={rename.isPending || !slugInput || slugInput === slug} data-testid="widget-slug-save">Save</Button>
        </div>
        <div className="text-xs mt-3">
          Direct URL: <a href={publicUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all" data-testid="widget-direct-url">{publicUrl}</a>
        </div>
      </Card>

      <Card className="rounded-sm bg-card border-border p-6" data-testid="widget-snippet-card">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">JavaScript Snippet</div>
        <div className="font-display text-xl font-extrabold mb-4">Add this to your site</div>
        <pre className="p-4 bg-black rounded-sm text-xs overflow-x-auto text-primary/90 font-mono whitespace-pre-wrap border border-border" data-testid="widget-snippet">{snippet}</pre>
        <Button variant="outline" className="rounded-sm mt-4" onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Snippet copied"); }} data-testid="widget-snippet-copy"><Copy className="h-4 w-4 mr-2" />Copy Snippet</Button>
      </Card>
    </div>
  );
}

function PreviewTab({ publicUrl }) {
  return (
    <div className="border border-border rounded-sm overflow-hidden bg-black" style={{ height: 640 }} data-testid="widget-preview">
      <iframe src={`${publicUrl}?embed=1`} className="w-full h-full border-0" title="Chat preview" />
    </div>
  );
}
